import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../../services/api';
import AuthLayout, { AuthLink } from './AuthLayout';
import { Button, Input, Alert } from '../../components/ui';

function ResetPassword() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const token = queryParams.get('token');
  const email = queryParams.get('email');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Shown against the field itself rather than only on submit.
  const mismatch = confirmPassword.length > 0 && newPassword !== confirmPassword;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage('');
    setSuccessMessage('');

    if (!newPassword || !confirmPassword) {
      setErrorMessage('Please fill in all fields.');
      setIsSubmitting(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      setErrorMessage('Passwords do not match.');
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await api.post('/user/reset-password', { email, token, newPassword });

      if (response.status === 200) {
        setSuccessMessage('Password successfully reset. Redirecting to sign in…');
        setTimeout(() => navigate('/login'), 3000);
      }
    } catch (error) {
      setErrorMessage(error.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AuthLayout
      title="Set a new password"
      subtitle={email ? `For ${email}` : 'Choose a new password for your account.'}
      footer={<AuthLink to="/login">Back to sign in</AuthLink>}
    >
      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        {successMessage && (
          <Alert variant="success" title="Password updated">
            {successMessage}
          </Alert>
        )}
        {errorMessage && <Alert variant="error">{errorMessage}</Alert>}

        <Input
          label="New password"
          type="password"
          name="newPassword"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          disabled={isSubmitting}
          autoComplete="new-password"
          hint="Use at least 8 characters."
        />

        <Input
          label="Confirm new password"
          type="password"
          name="confirmPassword"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          disabled={isSubmitting}
          autoComplete="new-password"
          error={mismatch ? 'Passwords do not match.' : undefined}
        />

        <Button type="submit" size="lg" fullWidth loading={isSubmitting} disabled={mismatch}>
          {isSubmitting ? 'Resetting…' : 'Reset password'}
        </Button>
      </form>
    </AuthLayout>
  );
}

export default ResetPassword;
